import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";

import { posCashRegisterCore } from "../../core/cashRegister";
import { formatCurrency } from "../../core/currency.core";
import { useTranslation } from "../../core/translation.core";
import CloseIcon from "../../icons/close.icon";
import { CashRegisterType } from "../../types/cashRegister";
import Link from "next/link";
import React from "react";

export default function ClosedRegister({ item }: { item: CashRegisterType }) {
  const { msg, t } = useTranslation();

  const { data } = useQuery({
    queryFn: () => posCashRegisterCore.single(item._id),
    queryKey: ["single-cash-register", item._id],
  });

  const STATS1 = [
    { name: t(msg.cash.openingCash), value: formatCurrency(item.openingCash, "HKD") },
    { name: t(msg.cash.startDate), value: format(new Date(item.createdAt), "EEE, yyyy-MM-dd HH:mm:ss") },
    { name: t(msg.cash.registerOpenBy), value: item.openedBy },
    { name: t(msg.cash.closingSummary), value: item.closingSummary },
  ];

  const STATS2 = [
    { name: t(msg.cash.closingCash), value: formatCurrency(item.closingCash, "HKD") },
    { name: t(msg.cash.endDate), value: item.endAt ? format(new Date(item.endAt), "EEE, yyyy-MM-dd HH:mm:ss") : "" },
    { name: t(msg.cash.registerCloseBy), value: item.closedBy },
    { name: t(msg.cash.closedTime), value: item.closedAt },
  ];

  return (
    <section className="bg-white dark:bg-her-darkblue2 space-y-6 px-1 py-5 lg:p-6 rounded-md shadow-md">
      <section className="grid grid-cols-1 md:grid-cols-[1.5fr_1fr_40px] gap-y-3 md:gap-8 justify-between items-start">
        <div className="grid grid-cols-[150px_auto] gap-3">
          {STATS1.map(({ name, value }) => (
            <React.Fragment key={name}>
              <h1 className="opacity-80">{name}: </h1>
              <p className="tracking-wide font-bold">
                {value}
              </p>
            </React.Fragment>
          ))}
        </div>

        <div className="grid grid-cols-[150px_auto] gap-3">
          {STATS2.map(({ name, value }) => (
            <React.Fragment key={name}>
              <h1 className="opacity-80">{name}: </h1>
              <p className="tracking-wide font-bold">
                {value as any}
              </p>
            </React.Fragment>
          ))}
        </div>

        <div
          data-label="closed"
          className="w-fit capitalize text-white text-sm lg:text-base flex gap-2 items-center px-2 py-1 rounded-md shadow-sm bg-[#F20000]"
        >
          <CloseIcon />
        </div>
      </section>

      <div className="bg-[#F5F9FE] dark:bg-gray-700 text-sm lg:text-base grid grid-cols-[1fr_1fr] gap-3 rounded-lg p-8">
        <div className="grid grid-cols-[150px_auto] gap-3">
          <h1>{t(msg.cash.totalSales)}:</h1>
          <p className="font-bold">{formatCurrency(item.totalSales, "HKD")}</p>

          <h1>{t(msg.cash.numberOfTransactions)}:</h1>
          <p>{item.transactions.filter((transaction) => transaction.type !== "withdrawal").length}</p>

          <h1 className="text-red-500">{t(msg.cash.totalWithdrawal)}:</h1>
          <p className="text-red-500 font-bold"> - {formatCurrency(item.totalWithdraw, "HKD")}</p>
        </div>

        <div className="grid grid-cols-[150px_auto] gap-3">
          <h1>{t(msg.cash.totalPaymentReceived)}:</h1>
          <p className="font-bold">{formatCurrency(item.totalCashReceived, "HKD")}</p>

          <h1>{t(msg.cash.totalRefund)}:</h1>
          <p className="font-bold"> - {formatCurrency(item.totalRefund, "HKD")}</p>
        </div>
      </div>

      {/* Transactions */}
      <div>
        {data?.data?.aggregatedTransactions?.map(({ totalAmount, transactions, type }) => (
          <div key={type} className="space-y-3 py-4" data-type={type}>
            <div className="flex justify-between border-b pb-1 font-bold text-lg">
              <h1 className="capitalize text-gray-400 text-[15px]">
                <span>{type} </span>
                <span>({transactions.length})</span>
              </h1>

              <p>
                {type === "withdrawal" ? (
                  <span className="!text-red-500"> - {formatCurrency(totalAmount, "HKD")}</span>
                ) : (
                  formatCurrency(totalAmount, "HKD")
                )}
              </p>
            </div>

            {transactions.map(({ amount, date, type, desc, orderId, orderNumber }) => (
              <div
                key={orderId + date}
                className="block space-y-2 lg:space-y-0 border-b pb-4 opacity-80 dark:opacity-100 lg:grid lg:grid-cols-3 justify-between gap-8"
              >
                <span className="tracking-wide">
                  {format(new Date(date), "yyyy-MM-dd")} {format(new Date(date), "HH:mm:ss")}
                </span>

                <div>
                  {type === "withdrawal" || type === "deposit" ? (
                    <span> {desc}</span>
                  ) : (
                    <Link
                      href={`/new-order/checkout?orderId=${orderId}`}
                      className="text-blue-500 underline"
                    >
                      {orderNumber}
                    </Link>
                  )}
                </div>

                <p className="opacity-80 justify-self-end">
                  {type === "withdrawal" ? (
                    <span className="!text-red-500"> - {formatCurrency(amount, "HKD")}</span>
                  ) : (
                    formatCurrency(amount, "HKD")
                  )}
                </p>
              </div>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}
